// frontend/packages/business-console/src/theme.js

import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@mui/material';

const ThemeModeContext = createContext({
  mode: 'light',
  toggleMode: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

const STORAGE_KEY = 'upryzze:console-theme';

// ── Design-token contract (handoff README section 11) ─────────────────
//
// The console redesign's single source of truth for color. Pages read
// `theme.custom.tokens.*` (via the `useTokens()` hook in
// components/primitives) instead of hardcoding hexes, so every surface
// stays consistent and a token change is one edit, not thirteen. The
// blue->purple gradients are IDENTICAL in both themes; everything else
// maps per mode. Keep in sync with the MUI palette below: palette
// drives the shell + MUI components, tokens drive bespoke page chrome.

const GRADIENT_ACCENT =
  'linear-gradient(120deg, #2563eb 0%, #7c3aed 55%, #ab2ddd 100%)';
const GRADIENT_HERO =
  'linear-gradient(120deg, #1e3a8a 0%, #2563eb 50%, #7c3aed 100%)';

const LIGHT_TOKENS = {
  gradient: GRADIENT_ACCENT,
  gradientHero: GRADIENT_HERO,
  // surfaces
  appBg: '#f8fafc',
  card: '#ffffff',
  subtle: '#f8fafc',
  subtleBlue: '#f7faff',
  hairline: '#f1f5f9',
  tableHead: '#fbfcfe',
  cardBorder: '#e6ebf1',
  grayBorder: '#e2e8f0',
  // text
  text: '#0f172a',
  textSecondary: '#475569',
  muted: '#64748b',
  faint: '#94a3b8',
  // semantic
  blue: '#2563eb',
  blueBg: '#eff4ff',
  blueBorder: '#dbe6ff',
  green: '#16a34a',
  greenText: '#15803d',
  greenBg: '#f0fdf4',
  greenBorder: '#bbf7d0',
  amber: '#d97706',
  amberText: '#b45309',
  amberBg: '#fffbeb',
  amberBorder: '#fde68a',
  red: '#dc2626',
  redBg: '#fff5f5',
  redBorder: '#fecaca',
  purple: '#7c3aed',
  purpleBg: '#f5f3ff',
  purpleBorder: '#ddd6fe',
  grayText: '#64748b',
  grayBg: '#f1f5f9',
  stripe: '#635bff',
  // elevation
  cardShadow: '0 18px 50px rgba(15,23,42,0.12)',
  cardShadowSoft: '0 8px 30px rgba(15,23,42,0.07)',
  smallShadow: '0 1px 2px rgba(15,23,42,.08)',
};

const DARK_TOKENS = {
  gradient: GRADIENT_ACCENT,
  gradientHero: GRADIENT_HERO,
  // surfaces
  appBg: '#0b1120',
  card: '#131c2e',
  subtle: '#0f172a',
  subtleBlue: '#0f172a',
  hairline: '#1f2937',
  tableHead: '#0f172a',
  cardBorder: '#1f2937',
  grayBorder: 'rgba(148,163,184,.3)',
  // text
  text: '#f1f5f9',
  textSecondary: '#cbd5e1',
  muted: '#94a3b8',
  faint: '#64748b',
  // semantic
  blue: '#60a5fa',
  blueBg: 'rgba(59,130,246,.16)',
  blueBorder: 'rgba(96,165,250,.35)',
  green: '#4ade80',
  greenText: '#4ade80',
  greenBg: 'rgba(22,163,74,.16)',
  greenBorder: 'rgba(74,222,128,.35)',
  amber: '#fbbf24',
  amberText: '#fbbf24',
  amberBg: 'rgba(217,119,6,.14)',
  amberBorder: 'rgba(251,191,36,.35)',
  red: '#f87171',
  redBg: 'rgba(248,113,113,.12)',
  redBorder: 'rgba(248,113,113,.35)',
  purple: '#c084fc',
  purpleBg: 'rgba(124,58,237,.18)',
  purpleBorder: 'rgba(192,132,252,.4)',
  grayText: '#94a3b8',
  grayBg: 'rgba(148,163,184,.12)',
  stripe: '#a5a0ff',
  // elevation
  cardShadow: '0 18px 50px rgba(15,23,42,0.30)',
  cardShadowSoft: '0 8px 30px rgba(0,0,0,0.35)',
  smallShadow: '0 1px 2px rgba(0,0,0,.4)',
};

const getStoredMode = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

const setStoredMode = (mode) => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore
  }
};

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(getStoredMode);

  const toggleMode = () => {
    const next = mode === 'light' ? 'dark' : 'light';
    setMode(next);
    setStoredMode(next);
  };

  const theme = useMemo(() => {
    const tokens = mode === 'light' ? LIGHT_TOKENS : DARK_TOKENS;
    return createTheme({
      palette: {
        mode,
        primary: {
          main: mode === 'light' ? '#2563eb' : '#60a5fa', // UpRyzze blue
        },
        secondary: {
          main: '#7c3aed',
        },
        success: { main: tokens.green },
        warning: { main: tokens.amber },
        error: { main: tokens.red },
        background: {
          // Redesign palette (handoff README section 11). Light keeps the
          // cool-white canvas; dark deepens to the design's near-black so
          // panels read with elevation against it.
          default: mode === 'light' ? '#f8fafc' : '#0b1120',
          paper: mode === 'light' ? '#ffffff' : '#131c2e',
        },
        divider: tokens.cardBorder,
        text: {
          primary: tokens.text,
          secondary: tokens.textSecondary,
        },
      },
      shape: {
        borderRadius: 10,
      },
      // Mobile design tokens (see docs/design/mobile-console-ux.md §6.2).
      // Centralized so phone surfaces — bottom nav, list rows, action rows,
      // tap targets — read as one product instead of each page hand-rolling
      // sizes. Access via theme.custom.mobile.*.
      custom: {
        mobile: {
          minTapTarget: 48, // a11y minimum touch target (px)
          bottomNavHeight: 64, // persistent bottom tab bar
          listRowHeight: 56, // tappable list rows
          actionRowHeight: 56, // Home quick-action rows
        },
        // Console redesign color contract. Pages read these via the
        // `useTokens()` hook (components/primitives) instead of hardcoding
        // hexes. Full light/dark maps are at the top of this file.
        tokens,
        gradient: tokens.gradient,
        gradientHero: tokens.gradientHero,
      },
      typography: {
        fontFamily:
          'Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
        h6: {
          fontWeight: 700,
        },
        subtitle2: {
          fontWeight: 600,
        },
        button: {
          textTransform: 'none',
          fontWeight: 700,
        },
      },
      components: {
        MuiButton: {
          defaultProps: {
            disableElevation: true,
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              borderRadius: 12,
            },
          },
        },
      },
    });
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,
      toggleMode,
    }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}
